/**
 * TILING — the half of the aligned pipeline both bases share.
 *
 * Word tiles from measured times, connected words joined, noise events placed,
 * and every remaining moment labelled silence or unclear. Keeping this
 * identical for the Gemini+aligner base and the WhisperX base is what makes the
 * engine toggle an honest A/B: only the source of the words and times differs.
 *
 * THE PARTITION INVARIANT LIVES AT THE BOTTOM OF THIS FILE.
 * The last loop of {@link tileFromWords} seals the seams: every tile starts
 * exactly where the previous one ended, the first starts at 0 and the last ends
 * at the recording's duration.
 *
 * THE PARTITION INVARIANT — every millisecond of a recording is labeled
 * exactly once. FOUR places in the app keep it, and they do two different
 * jobs:
 *   CHECK    `recut.assertTiles`          — throws when a recut does not tile
 *            `WaveLane.validateMoved`     — refuses an illegal drag
 *   CONSTRUCT `pipeline/alignedTiling.tileFromWords` — seals the seams as it
 *                                           builds the timeline
 *            `boundaryEdits.moveBoundary` — moving an edge TRANSFERS audio, so
 *                                           nothing is ever left unlabeled
 *
 * Precedence rules (the "who wins" table):
 *   - word STARTS come from the aligner (it measures; nothing overrides it),
 *   - word ENDS extend along continuing speech energy (CTC ends are tight and
 *     cut elongated sounds early — "مرحباااا" keeps its tail),
 *   - energy with no aligned word on it becomes noise/unclear (it is real
 *     audio someone must judge),
 *   - everything else is silence, labeled as such.
 */

import { round3 } from '../../lib/num';
import type { Segment, SegmentKind } from '../../types';
import { confidenceFromScore, type AlignedWord } from '../aligner';
import { extractMono } from '../audioSlicer';
import type { EnginePlan } from '../enginePlan';
import type { VerbatimToken } from '../gemini';
import { computeSpeechEnvelope } from '../vad';

/** A tile before it becomes a Segment row (no ids yet). */
export interface AlignedTile {
  startSec: number;
  endSec: number;
  heard: string;
  contextual: string;
  confidence: number;
  kind: SegmentKind;
  /**
   * Which stages produced this clip, in order. Written by every stage that
   * creates or rewrites a tile, so any clip can be traced back to the exact
   * step that invented it — no guessing about "was this Whisper or Gemini".
   */
  trace?: string[];
}

export interface AlignedTilesResult {
  tiles: AlignedTile[];
  /** ISO 639-3 language Gemini detected (drives the aligner's romanizer). */
  language: string;
  /** Tokens Gemini heard but the aligner could not place (noise events). */
  unplacedNoise: number;
}

/** Gaps shorter than this are absorbed into the preceding tile. */
const MIN_TILE_SEC = 0.15;
/** A gap counts as `unclear` when at least this much of it is speech energy. */
const GAP_SPEECH_FRAC = 0.4;
/** Floor for any emitted word tile. */
const MIN_WORD_SEC = 0.12;
/**
 * Words closer together than this are CONNECTED SPEECH and stay one tile —
 * "انا اسمي امجد" is one sentence-clip, not three impossible cuts. The human
 * verifies the sentence whole; the Cuts station's split (`cutOps.splitClip`)
 * decomposes it later, and its children re-enter the same verify loop.
 */
const JOIN_GAP_SEC = 0.18;

export interface BuildTilesArgs {
  buffer: AudioBuffer;
  /**
   * What to run. See services/enginePlan.ts.
   *
   * REQUIRED, and already normalised: `firstPass.runFirstPass` normalises it
   * exactly once at the entry to the run. It used to be optional here and
   * re-normalised against an inline copy of `DEFAULT_PLAN`, which meant two
   * places could disagree about what the default was.
   */
  plan: EnginePlan;
  /** 16 kHz mono WAV of the whole recording (the standard upload encode). */
  uploadWav: Blob;
  signal?: AbortSignal;
  /** Phase reporter: message + 0..1 fraction within this stage. */
  onPhase?: (message: string, frac: number) => void;
}

/**
 * The half of the pipeline both engines share: word tiles from measured
 * times, connected words joined, noise placed, and every remaining moment
 * labelled silence or unclear. Keeping this identical is what makes the
 * engine toggle an honest comparison.
 */
export function tileFromWords(
  args: BuildTilesArgs,
  alignable: VerbatimToken[],
  words: AlignedWord[],
  pendingNoise: Array<{ token: VerbatimToken; afterIndex: number }>,
  language: string,
): AlignedTilesResult {
  const { buffer } = args;
  const durationSec = buffer.duration;
  const unplacedNoise = pendingNoise.length;
  const env = computeSpeechEnvelope(extractMono(buffer), buffer.sampleRate);
  const hop = env.hopSec;
  const mask = env.mask;
  const speechAt = (sec: number): boolean => {
    const i = Math.floor(sec / hop);
    return i >= 0 && i < mask.length ? mask[i] !== 0 : false;
  };
  const speechFracIn = (a: number, b: number): number => {
    const i0 = Math.max(0, Math.floor(a / hop));
    const i1 = Math.min(mask.length, Math.ceil(b / hop));
    if (i1 <= i0) return 0;
    let on = 0;
    for (let i = i0; i < i1; i++) if (mask[i] !== 0) on++;
    return on / (i1 - i0);
  };

  // Word tiles: aligner starts, energy-extended ends.
  const wordTiles: AlignedTile[] = [];
  for (let i = 0; i < words.length; i++) {
    const w: AlignedWord = words[i];
    const token = alignable[i];
    const prevEnd = wordTiles.length ? wordTiles[wordTiles.length - 1].endSec : 0;
    const nextStart = i + 1 < words.length ? words[i + 1].start : durationSec;

    let start = Math.max(w.start, prevEnd);
    let end = Math.max(w.end, start + 0.02);
    // Extend the end while speech energy continues, up to the next word. This
    // is what keeps elongated sounds whole (a filler never ends mid-hum).
    let cursor = end;
    while (cursor + hop <= nextStart && speechAt(cursor)) cursor += hop;
    end = Math.max(end, Math.min(cursor, nextStart));
    // Enforce the floor, still never crossing the next word's measured start.
    if (end - start < MIN_WORD_SEC) end = Math.min(start + MIN_WORD_SEC, nextStart);
    if (end <= start) end = Math.min(start + 0.05, durationSec);

    wordTiles.push({
      startSec: round3(start),
      endSec: round3(Math.min(end, durationSec)),
      heard: token.text,
      contextual: token.text,
      confidence: confidenceFromScore(w.score),
      kind: token.kind,
      trace: [args.plan?.base === 'whisperx' ? 'whisperx' : 'gemini+aligner'],
    });
  }

  // Join connected speech: consecutive word/filler tiles separated by less
  // than JOIN_GAP_SEC fuse into one sentence tile (label = words in order).
  const joined: AlignedTile[] = [];
  for (const w of wordTiles) {
    const prev = joined[joined.length - 1];
    // Only WORDS run together. A filler, a noise or an unclear sound always
    // stands alone: fusing them into a sentence relabels them "word" and the
    // filler disappears from the dataset, which is exactly what it must not do.
    const bothWords = prev !== undefined && prev.kind === 'word' && w.kind === 'word';
    if (bothWords && w.startSec - prev.endSec < JOIN_GAP_SEC) {
      prev.endSec = w.endSec;
      prev.heard = `${prev.heard} ${w.heard}`.trim();
      prev.contextual = prev.heard;
      prev.confidence = Math.min(prev.confidence, w.confidence);
      prev.trace = [...(prev.trace ?? []), 'joined'];
      continue;
    }
    joined.push({ ...w });
  }

  // Noise events Gemini heard: each goes into the gap between the words it
  // was heard between, taking the sounding part of that gap. Without this the
  // cough's audio is silently swallowed by whichever word sits next to it.
  const noiseTiles: AlignedTile[] = [];
  for (const { token, afterIndex } of pendingNoise) {
    const before = afterIndex >= 0 ? wordTiles[Math.min(afterIndex, wordTiles.length - 1)] : undefined;
    const after = wordTiles[afterIndex + 1];
    const gapStart = before ? before.endSec : 0;
    const gapEnd = after ? after.startSec : durationSec;
    if (gapEnd - gapStart < 0.06) continue; // no room: the word tiles already cover it
    // take the sounding stretch inside the gap when there is one
    let s = gapStart;
    let e = gapEnd;
    let cursor = gapStart;
    let bestS = -1;
    let bestE = -1;
    while (cursor < gapEnd) {
      if (speechAt(cursor)) {
        const runStart = cursor;
        while (cursor < gapEnd && speechAt(cursor)) cursor += hop;
        if (cursor - runStart > bestE - bestS) {
          bestS = runStart;
          bestE = cursor;
        }
      } else cursor += hop;
    }
    if (bestS >= 0 && bestE - bestS >= 0.06) {
      s = bestS;
      e = Math.min(bestE, gapEnd);
    }
    noiseTiles.push({
      startSec: round3(s),
      endSec: round3(e),
      heard: token.text || 'noise',
      contextual: token.text || 'noise',
      confidence: 0.5,
      kind: 'noise',
      trace: ['gemini:noise-event'],
    });
  }
  if (noiseTiles.length) {
    joined.push(...noiseTiles);
    joined.sort((a, b) => a.startSec - b.startSec);
  }

  // Complement pass: every gap becomes silence / unclear, or is absorbed.
  const tiles: AlignedTile[] = [];
  let cursorSec = 0;
  const pushGap = (from: number, to: number) => {
    const span = to - from;
    if (span <= 0) return;
    if (span < MIN_TILE_SEC) {
      // Too small to stand alone: glue it onto the previous tile (or the next
      // one's start when the file opens with a sliver).
      if (tiles.length) tiles[tiles.length - 1].endSec = round3(to);
      else if (joined.length) joined[0].startSec = round3(from);
      return;
    }
    const frac = speechFracIn(from, to);
    tiles.push(
      frac >= GAP_SPEECH_FRAC
        ? {
            startSec: round3(from),
            endSec: round3(to),
            heard: 'unclear',
            contextual: 'sound with no transcription — listen and label it',
            confidence: 0.2,
            kind: 'unclear',
            trace: ['vad:unlabelled-sound'],
          }
        : {
            startSec: round3(from),
            endSec: round3(to),
            heard: 'silence',
            contextual: frac > 0.1 ? 'mostly quiet (some faint sound)' : 'quiet',
            confidence: 0.8,
            kind: 'silence',
            trace: ['vad:silence'],
          },
    );
  };

  for (const w of joined) {
    pushGap(cursorSec, w.startSec);
    // pushGap may have moved this tile's start (opening sliver case).
    const start = Math.max(w.startSec, cursorSec);
    tiles.push({ ...w, startSec: round3(start) });
    cursorSec = w.endSec;
  }
  pushGap(cursorSec, durationSec);

  /* -- 4. partition invariants -------------------------------------- */
  for (let i = 0; i < tiles.length; i++) {
    const t = tiles[i];
    if (i > 0) {
      const prev = tiles[i - 1];
      if (t.startSec < prev.endSec) t.startSec = prev.endSec; // touching, never overlapping
      if (t.startSec !== prev.endSec) t.startSec = prev.endSec; // and never gapping
    }
    if (t.endSec <= t.startSec) t.endSec = round3(Math.min(t.startSec + 0.05, durationSec));
  }
  tiles[0].startSec = 0;
  tiles[tiles.length - 1].endSec = round3(durationSec);

  return { tiles, language, unplacedNoise };
}


/** Tiles → Segment records: word entities minted, version names assigned. */
export function tilesToSegments(
  tiles: AlignedTile[],
  taskId: string,
  makeId: () => string,
  taskCode?: string,
): Segment[] {
  return tiles.map((t, index) => {
    const isPlaceholder =
      t.kind === 'silence' || ((t.kind === 'unclear' || t.kind === 'noise') && !t.heard.trim());
    const words =
      isPlaceholder || t.heard === 'silence' || t.heard === 'unclear'
        ? []
        : t.heard.trim().split(/\s+/).filter(Boolean).map((text) => ({ id: makeId(), text }));
    return {
      id: makeId(),
      taskId,
      index,
      startSec: t.startSec,
      endSec: t.endSec,
      heard: t.heard,
      contextual: t.contextual,
      confidence: t.confidence,
      kind: t.kind,
      verify: 'pending' as const,
      words,
      trace: t.trace,
      ...(taskCode ? { name: `${taskCode}.${index + 1}` } : {}),
    };
  });
}
